import React from 'react';
import { HTTP } from '@helpcenter/constants';
import { ElementErrorUI } from '@helpcenter/layout-primitives-util/src/element-error-ui';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { BreadcrumbsElement } from '@atlassian/help-center-breadcrumbs-element';
import { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import EditorViewerComponent from '@atlassian/help-center-editor-element/src/ui/editor-view-renderer';
import { HeroElement } from '@atlassian/help-center-hero-element';
import { NoContent } from '@atlassian/no-content-element';
import type { customPageElementViewRendererFragment$key } from './__generated__/customPageElementViewRendererFragment.graphql';
import messages from './messages';

export interface CustomPageElementViewRendererProps {
	element: customPageElementViewRendererFragment$key;
	parentSubsectionId: string;
}
// TODO: Add VR tests for this component once API integration is ready
const CustomPageElementViewRenderer = ({
	element: elementKey,
}: CustomPageElementViewRendererProps) => {
	di(EditorViewerComponent, HeroElement);
	const { formatMessage } = useIntl();
	const elementData = useFragment(
		graphql`
			fragment customPageElementViewRendererFragment on HelpLayoutElement {
				__typename
				... on HelpLayoutEditorElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the rich-text package's entry point.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...editorViewRendererFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutHeroElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
					# It does not report an error if we consume this from the module - help-center-hero-element, directly.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...heroElementFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutBreadcrumbElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
					# It does not report an error if we consume this from the module - help-center-hero-element, directly.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...breadcrumbsElementFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutNoContentElement {
					__typename
				}
				... on QueryError {
					identifier
					message
					extensions {
						statusCode
					}
				}
			}
		`,
		elementKey,
	);

	switch (elementData.__typename) {
		case 'HelpLayoutEditorElement': {
			return <EditorViewerComponent editorElement={elementData} />;
		}
		case 'HelpLayoutBreadcrumbElement': {
			return <BreadcrumbsElement breadcrumbsElement={elementData} />;
		}
		case 'HelpLayoutHeroElement': {
			return <HeroElement heroElement={elementData} hideSearch hideTitle />;
		}
		case 'HelpLayoutNoContentElement': {
			return <NoContent />;
		}
		case 'QueryError': {
			const elementIdentifier =
				(elementData as unknown as { identifier: string }).identifier || 'UNKNOWN';
			// Warning: the below non-isomorphic way of coding is not recommended and please do not adopt this practice elsewhere.
			if (__SERVER__) {
				return (
					<ElementErrorUI
						errorMessage={formatMessage(messages.errorMessage)}
						elementType={
							elementIdentifier === 'UNKNOWN' ? elementData.__typename : elementIdentifier
						}
					/>
				);
			}
			let statusCode = HTTP.SERVER;
			const message = elementData?.message;
			if (
				elementData.extensions &&
				Array.isArray(elementData.extensions) &&
				elementData.extensions.length > 0
			) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
				statusCode = elementData.extensions[0].statusCode;
			}
			throw new FetchError(message || `${elementIdentifier} content failed to load`, statusCode);
		}
		default: {
			return null;
		}
	}
};

export default CustomPageElementViewRenderer;
